import { useState, type FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { BookOpen, Loader2 } from 'lucide-react'

import { useAuth } from '@/lib/auth/use-auth'
import { cn } from '@/lib/utils'

export default function LoginPage() {
  const { session, loading, signIn } = useAuth()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (loading) return <AuthSplash />

  if (session) {
    // Return the user to whatever they were trying to reach before the redirect.
    const from = (location.state as { from?: string } | null)?.from ?? '/'
    return <Navigate to={from} replace />
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signIn(email, password)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not sign in.')
      setSubmitting(false)
    }
    // On success the auth listener swaps the route out; leaving `submitting`
    // true keeps the button disabled through that transition so a double-click
    // cannot fire a second sign-in.
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center gap-2">
          <BookOpen className="size-7 text-accent" aria-hidden />
          <span className="text-xl font-semibold tracking-tight text-fg">
            Company <span className="text-fg-muted">Balance Sheet</span>
          </span>
        </div>

        <div className="rounded-card border border-border bg-surface p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-fg">Sign in</h1>
          <p className="mt-1 text-sm text-fg-secondary">
            Use the email address your workspace was created with.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-fg">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
                aria-describedby={error ? 'login-error' : undefined}
                className={cn(
                  'h-10 w-full rounded-control border border-border-strong bg-surface px-3',
                  'text-sm text-fg outline-none transition-colors',
                  'placeholder:text-fg-subtle focus:border-accent disabled:opacity-60',
                )}
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-fg">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
                aria-describedby={error ? 'login-error' : undefined}
                className={cn(
                  'h-10 w-full rounded-control border border-border-strong bg-surface px-3',
                  'text-sm text-fg outline-none transition-colors',
                  'placeholder:text-fg-subtle focus:border-accent disabled:opacity-60',
                )}
              />
            </div>

            {error && (
              <p
                id="login-error"
                role="alert"
                className="rounded-control bg-danger-subtle px-3 py-2 text-sm text-danger"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || email === '' || password === ''}
              className={cn(
                'flex h-10 w-full items-center justify-center gap-2 rounded-control',
                'bg-accent text-sm font-medium text-fg-on-accent transition-colors',
                'hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              {submitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-fg-muted">
          Accounts are created by a workspace owner. Contact them if you cannot sign in.
        </p>
      </div>
    </div>
  )
}

export function AuthSplash({ message }: { message?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="size-6 animate-spin text-fg-muted" aria-hidden />
        <p className="text-sm text-fg-muted">{message ?? 'Loading…'}</p>
      </div>
    </div>
  )
}
